import { GridFilterItem } from '@mui/x-data-grid-pro';

import { STRUCTURE_FILTER_FIELD_DEFINITIONS } from './structureFilterDefinitions';
import { StructureFilterState } from '../../../components/molecules/StructureFilterBar';
import { applyColumnOrder, ColumnDef } from '../../../components/organisms/StructureTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { columnSelectionItems } from '../../../constants/structure';
import { advancedFiltersFromModel } from '../../../utils/advancedFilterFromModel';
import { buildAdvancedFilterItems } from '../../../utils/filterModel';

export interface LabelValue {
  label: string;
  value: string;
}

/**
 * The structure type the list opens on. The tree holds every level from group
 * down to terminal, and outlets are the level people actually work with — 150k
 * unfiltered rows is not a useful first screen.
 *
 * Matches a `nodeType.name` exactly, since the Location Type options are built
 * straight from the structure-type list. If the type is ever renamed the filter
 * silently matches nothing, which `structureListFilters.tests.ts` guards.
 */
export const DEFAULT_LOCATION_TYPE = 'Outlet';

/**
 * Baseline filter state shown on a fresh list load. Active defaults to "Active"
 * so users only see live locations unless they explicitly add "Inactive" (or
 * pick both), and Location Type defaults to Outlet.
 */
export const EMPTY_FILTERS: StructureFilterState = {
  locationTypes: [DEFAULT_LOCATION_TYPE],
  contracts: [],
  activeStatus: ['true'],
  locationId: '',
  multiSearch: '',
  advancedFilters: [],
};

/**
 * The first request's filter, derived from `EMPTY_FILTERS` rather than written
 * out again — the two seed different things (the strip and the query) and a
 * hand-maintained copy drifts, leaving the chips describing a filter the rows
 * were never fetched with. Declared as a getter because `buildFilterModel` is
 * defined further down the module.
 */
export const getDefaultFilterItems = (): GridFilterItem[] => buildFilterModel(EMPTY_FILTERS).items;

// The address is broken into its parts rather than shown as the pre-joined
// `mainAddress` line, so each piece sorts and filters on its own.
//
// Location Type is off (the list is already filtered to one type by default, so
// the column repeats it on every row), as are Main Contact, the combined
// Client/Contract/Brand cell and Active. All still one tick away in the picker.
const DEFAULT_VISIBLE_FIELDS = [
  'id',
  'name',
  'mainAddressLine1',
  'mainAddressSuburb',
  'mainAddressPostcode',
  'mainAddressState',
  'parentName',
  'contract',
  'regionType',
  'zone',
];

/** Compact columns that get a fixed pixel width and do not flex. */
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  id: 70,
  isActive: 80,
  nodeTypeName: 110,
  // The short split-address parts hold a handful of characters ("3000",
  // "Victoria", "Australia"), so they take a fixed width rather than a flex
  // share — otherwise they'd claim as much room as a full street line.
  mainAddressPostcode: 90,
  mainAddressState: 140,
  mainAddressCountry: 120,
};

/**
 * Flex weights for columns that share remaining space. A column with
 * flex: 3 gets three times the space of a flex: 1 column. Columns not
 * listed here default to flex: 1.
 */
const DEFAULT_COLUMN_FLEX: Record<string, number> = {
  name: 2,
  mainAddress: 3,
  allAddresses: 3,
  parentName: 2,
  mainContact: 2,
  allContacts: 3,
  clientContractBrand: 2,
};

/**
 * Minimum pixel widths for flex columns. When too many columns are
 * visible and the total of all minimums exceeds the container, the
 * table scrolls horizontally instead of squishing content.
 */
const DEFAULT_COLUMN_MIN_WIDTHS: Record<string, number> = {
  name: 180,
  mainAddress: 200,
  allAddresses: 200,
  parentName: 160,
  mainContact: 160,
  allContacts: 200,
  clientContractBrand: 160,
  contract: 120,
  client: 120,
  brand: 120,
  referenceNo1: 130,
  referenceNo2: 130,
  referenceNo3: 130,
  notes: 140,
  tradingHrs: 160,
  // The free-text split-address parts stay on the default flex: 1 (they are one
  // component each, not the whole `mainAddress` blob that earns flex: 3) and
  // just take a floor wide enough to read.
  mainAddressLine1: 180,
  mainAddressLine2: 140,
  mainAddressSuburb: 140,
  mainAddressCity: 140,
};

const DEFAULT_COLUMN_HEADER_LABELS: Record<string, string> = {
  id: 'ID',
  clientContractBrand: 'Client/Contract/Brand',
};

/**
 * Build the initial column-selection state for the API. This mirrors the raw
 * `columnSelectionItems` constant but ensures every field in
 * `DEFAULT_VISIBLE_FIELDS` has `value: true` so the API returns data for every
 * column the table is rendering on first load.
 */
export const buildInitialColumnSelection = (): ColumnSelectionItem[] =>
  columnSelectionItems.map((group) => ({
    ...group,
    value: group.value.map((col) => ({
      ...col,
      value: DEFAULT_VISIBLE_FIELDS.includes(col.field),
    })),
  }));

export const buildColumnDefs = (): ColumnDef[] => {
  const defs: ColumnDef[] = [];
  for (const group of columnSelectionItems) {
    for (const col of group.value) {
      // printLabel is handled as a row-level action, not a column cell
      if (col.field === 'printLabel') continue;
      defs.push({
        key: col.field,
        label: col.title,
        group: group.title,
        visible: DEFAULT_VISIBLE_FIELDS.includes(col.field),
        ...(DEFAULT_COLUMN_HEADER_LABELS[col.field] !== undefined
          ? { headerLabel: DEFAULT_COLUMN_HEADER_LABELS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_WIDTHS[col.field] !== undefined
          ? { width: DEFAULT_COLUMN_WIDTHS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_FLEX[col.field] !== undefined
          ? { flex: DEFAULT_COLUMN_FLEX[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_MIN_WIDTHS[col.field] !== undefined
          ? { minWidth: DEFAULT_COLUMN_MIN_WIDTHS[col.field] }
          : {}),
      });
    }
  }
  return applyColumnOrder(
    defs,
    DEFAULT_VISIBLE_FIELDS.map((field) => ({ field }))
  );
};

export const filterModelToFilterState = (model: {
  items: GridFilterItem[];
}): StructureFilterState => {
  const state: StructureFilterState = {
    locationTypes: [],
    contracts: [],
    activeStatus: [],
    locationId: '',
    multiSearch: '',
    advancedFilters: [],
  };

  // Predicates outside the quick-filter slots become Add Filter chips;
  // `advancedFiltersFromModel` owns that reconstruction.
  const rest: GridFilterItem[] = [];

  for (const item of model.items) {
    if (item.field === 'nodeTypeName') {
      if (item.operator === 'isAnyOf' && Array.isArray(item.value)) {
        state.locationTypes.push(...item.value.map(String));
      } else if (item.operator === 'equals' && item.value) {
        state.locationTypes.push(String(item.value));
      }
    } else if (item.field === 'contract') {
      if (item.operator === 'isAnyOf' && Array.isArray(item.value)) {
        state.contracts.push(...item.value.map(String));
      } else if (item.operator === 'equals' && item.value) {
        state.contracts.push(String(item.value));
      }
    } else if (item.field === 'isActive' && item.operator === 'is' && item.value != null) {
      state.activeStatus.push(String(item.value) === 'true' ? 'true' : 'false');
    } else if (item.field === 'id' && item.value) {
      state.locationId = String(item.value);
    } else {
      rest.push(item);
    }
  }

  state.advancedFilters = advancedFiltersFromModel(rest, STRUCTURE_FILTER_FIELD_DEFINITIONS);

  return state;
};

export const buildFilterModel = (filters: StructureFilterState): { items: GridFilterItem[] } => {
  const items: GridFilterItem[] = [];

  if (filters.locationId) {
    items.push({ field: 'id', operator: 'equals', value: filters.locationId });
  }

  if (filters.locationTypes.length === 1) {
    items.push({ field: 'nodeTypeName', operator: 'equals', value: filters.locationTypes[0] });
  } else if (filters.locationTypes.length > 1) {
    items.push({ field: 'nodeTypeName', operator: 'isAnyOf', value: filters.locationTypes });
  }

  if (filters.contracts.length === 1) {
    items.push({ field: 'contract', operator: 'equals', value: filters.contracts[0] });
  } else if (filters.contracts.length > 1) {
    items.push({ field: 'contract', operator: 'isAnyOf', value: filters.contracts });
  }

  // Active status: users must opt into "Inactive" explicitly. Picking exactly
  // one narrows to it; picking both (or neither, as a fallback) returns all
  // active+inactive with no isActive filter sent to the API.
  if (filters.activeStatus.length === 1) {
    items.push({ field: 'isActive', operator: 'is', value: filters.activeStatus[0] === 'true' });
  }

  // `multiSearch` is NOT a filterModel item — it travels as the dedicated
  // `multiSearchQuery` request param (matches node name OR referenceNo1),
  // wired through useStructureListData. See StructureQueryOptions.

  for (const adv of filters.advancedFilters) {
    if (adv.operator === 'between') {
      const fromValue = adv.dateFrom ?? adv.value;
      const toValue = adv.dateTo ?? adv.value2 ?? '';
      if (adv.type === 'date') {
        if (fromValue) items.push({ field: adv.dataKey, operator: 'onOrAfter', value: fromValue });
        if (toValue) items.push({ field: adv.dataKey, operator: 'onOrBefore', value: toValue });
      } else {
        if (fromValue) items.push({ field: adv.dataKey, operator: '>=', value: fromValue });
        if (toValue) items.push({ field: adv.dataKey, operator: '<=', value: toValue });
      }
      continue;
    }
    // Structures talks to a lib-framework BFF, which collapses repeated params
    // but supports `isNotAnyOf` — a multi-value `Is not` emits one such item.
    items.push(...buildAdvancedFilterItems(adv, 'isNotAnyOf'));
  }

  return { items };
};

export const buildLocationTypeOptions = (
  data: Array<{ name: string; isActive?: boolean }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data
    .filter((s) => s.isActive !== false)
    .map((s) => ({ label: s.name, value: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildContractOptions = (
  data: Array<{ name: string; isActive?: boolean }> | undefined
): LabelValue[] => {
  if (!data) return [];
  return data
    .filter((c) => c.isActive !== false)
    .map((c) => ({ label: c.name, value: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const ACTIVE_STATUS_OPTIONS: LabelValue[] = [
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
];
